import React from "react";
import { getSdk } from "../services/sdkService";
import { i18n } from "../services/i18nService";
import { getGlobalButtonFocusPath, getConfigForModalWithCloseButton } from "../errors/globalButtons";

import formatDate from "./formatDate";
import { getUserPayments, activateChannel as activate } from "../services/billingService";
import { PremiumIconStyled } from "../styles/shared-styles";
import { getAppState } from "../state";

const STARZ = "starz";
const t20AddOnName = 't20wcpass';
const cricketAddOnName = 'criclife';
const SELF_DEACTIVATED = "self-deactivated";
const CANCEL_BUTTON_MODAL_FOCUS_PATH = "cancelButtonModalFocusPath";
const CONFIRM_BUTTON_MODAL_FOCUS_PATH = "confirmButtonModalFocusPath";
const CLOSE = "close";
const VISA = "visa";
const MASTERCARD = "mastercard";
const FORMAT_DATE = "MMDDYYYY";
const GLOBAL_CHANNELS_KEY = "global.channels";
const ACTIVATE_CHANNEL_KEYS = `${GLOBAL_CHANNELS_KEY}.activate`;
const GLOBAL_ERRORS_KEY = "global.errors";
const FIRST_NUMBER_OF_VISA = "4";
const FIRST_NUMBER_OF_MC = "5";
const NEXT_BILL_WITHOUT_PROMOTION_IN_DAYS = 30;

export function getPaymentMethod(subscription, paymentMethod) {
  return subscription.paymentMethods.find(
    method => method.name === paymentMethod || method.paymentMethod === paymentMethod
  );
}

async function getUserPaymentData(subscription) {
  const { paymentMethod } = await getUserPaymentMethodInformation();
  const userPaymentMethod = getPaymentMethod(subscription, paymentMethod);
  return userPaymentMethod.paymentPlans[0];
}

function getDateTrialEnd(trialDays) {
  const date = new Date();
  date.setDate(date.getDate() + trialDays);
  return formatDate(FORMAT_DATE, date);
}

async function activateChannel(subscriptions = [], addonName, focusPath) {
  if (!!subscriptions.length) {
    const { pauseNavigation, resumeNavigation, stateActions } = this.props;
    try {
      const sdk = await getSdk();
      const subscription = getSubscription(subscriptions, addonName);
      this.setState({ isLoading: true });
      closeModal.call(this, focusPath);
      pauseNavigation();
      const { id } = (await getUserPaymentData.call(this, subscription)) || {};
      await activate({
        addonSubscriptionData: {
          paymentPlanId: id
        },
        addonName
      });
      const {
        settings: { addons }
      } = await sdk.userManager.refreshCachedUserData();
      stateActions.setAddons(addons);
      showSimpleModal.call(
        this,
        i18n.get(`${ACTIVATE_CHANNEL_KEYS}.activationSuccessfullyText`),
        i18n.get(
          `${ACTIVATE_CHANNEL_KEYS}.activationSuccessfullyTitle`,
          i18n.get(`${GLOBAL_CHANNELS_KEY}.${addonName}`)
        ),
        focusPath,
        <PremiumIconStyled name="premiumContentFilled" />
      );
    } catch (e) {
      showSimpleModal.call(this, i18n.get(`${GLOBAL_ERRORS_KEY}.genericError`), null, focusPath);
    } finally {
      resumeNavigation();
    }
  }
}

async function getUserPaymentMethodInformation() {
  const { paymentMethods } = await getUserPayments();
  if (!!paymentMethods.length) {
    const { pin, paymentMethod } = paymentMethods[0];
    return {
      pin,
      paymentMethod
    };
  }
  return {};
}

export function getSubscription(subscriptions = [], addonName) {
  return (subscriptions && !!subscriptions.length && subscriptions.find(subs => subs.name === addonName)) || {};
}

async function getActivateModalChannelTitle(subscriptions = [], addonName) {
  const subscription = getSubscription(subscriptions, addonName);
  const { isPromoApplicable } = subscription;
  const { promotionDuration } = (await getUserPaymentData.call(this, subscription)) || {};
  return isPromoApplicable
    ? i18n.get(
        `${ACTIVATE_CHANNEL_KEYS}.popupPromoTitle`,
        i18n.get(`${GLOBAL_CHANNELS_KEY}.${addonName}`),
        promotionDuration
      )
    : i18n.get(`${ACTIVATE_CHANNEL_KEYS}.popupTitle`, i18n.get(`${GLOBAL_CHANNELS_KEY}.${addonName}`));
}

async function getActivateModalChannelBody(subscriptions = [], addonName) {
  const { pin } = await getUserPaymentMethodInformation();
  const subscription = getSubscription(subscriptions, addonName);
  const { isPromoApplicable } = subscription;
  const { currency, grossAmount, promotionDurationInDays } = (await getUserPaymentData.call(this, subscription)) || {};
  const cardType = pin[0] === FIRST_NUMBER_OF_VISA ? VISA : pin[0] === FIRST_NUMBER_OF_MC ? MASTERCARD : "";
  const cardTypeText = i18n.get(`global.creditCards.${cardType}`);
  return isPromoApplicable
    ? `${i18n.get(
        `${ACTIVATE_CHANNEL_KEYS}.popupPromoText`,
        getDateTrialEnd(promotionDurationInDays),
        grossAmount,
        currency,
        cardTypeText,
        pin
      )}`
    : `${i18n.get(`${ACTIVATE_CHANNEL_KEYS}.paymentMethod`, cardTypeText, pin)}${i18n.get(
        `${ACTIVATE_CHANNEL_KEYS}.today`,
        currency,
        grossAmount
      )}${i18n.get(
        `${ACTIVATE_CHANNEL_KEYS}.nextBill`,
        getDateTrialEnd(NEXT_BILL_WITHOUT_PROMOTION_IN_DAYS),
        currency,
        grossAmount
      )}${i18n.get(`${ACTIVATE_CHANNEL_KEYS}.popupSubtitle`)}${i18n.get(`${ACTIVATE_CHANNEL_KEYS}.termsOfUseText`)}`;
}

function closeModal(focusPath) {
  const {
    stateActions: { hideGlobalModal },
    setFocus
  } = this.props;
  hideGlobalModal();
  setFocus(focusPath);
}

function showSimpleModal(text, title = null, focusPath, topIcon) {
  const { stateActions, setFocus } = this.props;
  this.setState({ isLoading: false });
  stateActions.showGlobalModal(
    getConfigForModalWithCloseButton(
      {
        ...(title && { title }),
        text,
        ...(topIcon && { topIcon })
      },
      closeModal.bind(this, focusPath)
    )
  );
  setFocus(getGlobalButtonFocusPath(CLOSE));
}

async function isUserPaymentMethodCompatible(subscriptions, addonName) {
  const subscription = getSubscription(subscriptions, addonName);
  const { paymentMethod } = await getUserPaymentMethodInformation();
  return !!getPaymentMethod(subscription, paymentMethod);
}

export function isSelfDeactivatedUser() {
  const {
    applicationState: { requestedState }
  } = this.props;
  return requestedState.toLowerCase() === SELF_DEACTIVATED;
}

export function showChannelSubscriptionErrorPopup(focusPath) {
  showSimpleModal.call(this, i18n.get(`${GLOBAL_ERRORS_KEY}.account.channelError`), null, focusPath);
  return false;
}

export function showChannelSubscriptionErrorPopupNew(closeModal) {
  const { stateActions, setFocus } = this.props;
  stateActions.showGlobalModal(
    getConfigForModalWithCloseButton(
      {
        text: i18n.get("global.errors.account.channelError")
      },
      closeModal
    )
  );
  setFocus(getGlobalButtonFocusPath("close"));
  return false;
}

export function showSportPackageSubscriptionErrorPopup(closeModal,packageName) {
  const { stateActions, setFocus } = this.props;
console.log(packageName);
    if (packageName==="undefined" || packageName===undefined)
    {
      packageName="Monthly Premium Plan";
      stateActions.showGlobalModal(
        getConfigForModalWithCloseButton(
          {
            text: i18n.get(`global.errors.account.sportPackageErrorund`,packageName)
          },
          closeModal,
          packageName
        )
      );
    }
else {

  stateActions.showGlobalModal(
    getConfigForModalWithCloseButton(
      {
        text: i18n.get(`global.errors.account.sportPackageError`,packageName)
      },
      closeModal,
      packageName
    )
  );
  }
  setFocus(getGlobalButtonFocusPath("ok"));
  return false;
}

export function isChannelAvailable(subscriptions = [], addonName) {
  return !!Object.keys(getSubscription(subscriptions, addonName)).length;
}

export function isUserSubscribedToChannel(addons = [], name) {
  if (isAddonContent(name)) {
    return addons.find(addon => addon.name === name && addon.status.toLowerCase() === "active");
  }
  return true;
}
export async function isUserPaymentMethodValidToChannelSubscription(addonName) {
  const {
    pauseNavigation,
    resumeNavigation,
    applicationState: { subscriptions }
  } = this.props;
  try {
    this.setState({ isLoading: true });
    pauseNavigation();
    const userBilling = await getUserPayments();
    const { paymentMethod } = await getUserPaymentMethodInformation();
    return (
      !!getPaymentMethod(userBilling, paymentMethod) && (await isUserPaymentMethodCompatible(subscriptions, addonName))
    );
  } catch (error) {
    console.log(error);
    return false;
  } finally {
    resumeNavigation();
  }
}

export async function showActivateChannelModal(addonName, focusPath) {
  const {
    stateActions,
    setFocus,
    applicationState: { subscriptions }
  } = this.props;
  this.setState({ isLoading: false });
  stateActions.showGlobalModal({
    modalTitle: await getActivateModalChannelTitle.call(this, subscriptions, addonName),
    modalText: await getActivateModalChannelBody.call(this, subscriptions, addonName),
    buttons: [
      {
        label: i18n.get("global.cancel"),
        focusPath: CANCEL_BUTTON_MODAL_FOCUS_PATH,
        buttonHandler: closeModal.bind(this, focusPath)
      },
      {
        label: i18n.get("global.confirm"),
        focusPath: CONFIRM_BUTTON_MODAL_FOCUS_PATH,
        buttonHandler: activateChannel.bind(this, subscriptions, addonName, focusPath)
      }
    ],
    dismissable: false
  });
  setFocus(CANCEL_BUTTON_MODAL_FOCUS_PATH);
}

export function isAddonContent(name) {
  return name && name !== STARZ;
}

export function isUserSubscribedToSpSportsChannel(addons = [], name) {
  if (isAddonContent(name)) {
    return addons.find(addon => addon.name === name && addon.status.toLowerCase() === "active");
  }
  return true;
}

export function isMainPackageAddOn(addOnName) {
  return getAddOnByName(addOnName)?.configuration?.isMainPackage || false;
}

export function isStandAloneAddon(addOnName) {
  return getAddOnByName(addOnName)?.configuration?.isStandalone || false;
}

export function getAddOnByName(addOnName) {
  const addOnsList = getAppState().subscriptions?.filter(data => data?.name === addOnName) || [];
  if (addOnsList?.length > 0) {
    return addOnsList[0];
  }
  return null;
}

export function isT20AddOn(addOnName) {
  const t20AddOn = getAddOnByName(addOnName);
  if (t20AddOn?.name === t20AddOnName) {
    return true;
  }
  return false;
}


export function isCricketAddOn(addOnName) {
  const cricketAddon = getAddOnByName(addOnName);
  if (cricketAddon?.name === cricketAddOnName) {
    return true;
  }
  return false;
}

export function isT20ChildSubscribed(addOnName) {
  const cricketAddOn = getAddOnByName(addOnName);
  const cricketAddOnActive = getAppState().addons.find(addon => (addon.name === cricketAddOnName) && addon.status.toLowerCase() === "active")
  if (!cricketAddOnActive) {
    if (cricketAddOn?.configuration?.subscriptionAcquires) {
      const childAddOn = cricketAddOn?.configuration.subscriptionAcquires !== "" ? cricketAddOn?.configuration.subscriptionAcquires : "";
      // if child is not there, then by pass the logic
      if (!childAddOn) {
        return true;
      }
      const isChildActive = getAppState().addons.find(addon => (addon.name === childAddOn) && addon.status.toLowerCase() === "active")
      return isChildActive ? true : false;
    }
    // is stand alone true
    return true;
  }
  return true;
}

export function CricChildAddOn(addOnName) {
  const cricketAddOn = getAddOnByName(addOnName);
  const childAddOnName = cricketAddOn.configuration.subscriptionAcquires !== "" ? cricketAddOn.configuration.subscriptionAcquires : "";
  if (childAddOnName) {
    return getAddOnByName(childAddOnName)
  }
  return null;
}

export function isCricketAcquiresFrom(addOnName) {
  const cricketAddOn = getAddOnByName(addOnName);
  const childAddOnName = cricketAddOn.configuration.subscriptionAcquires !== "" ? cricketAddOn.configuration.subscriptionAcquires : "";
  if (childAddOnName) {
    return true;
  }
  return false;
}

export function isT20ParentSubscribed(addOnName) {
  const t20AddOn = getAddOnByName(addOnName);
  if (t20AddOn.configuration.subscriptionInherits) {
    const parentAddOns = t20AddOn.configuration.subscriptionInherits !== "" ? t20AddOn.configuration.subscriptionInherits?.split(',') : [];
    if (parentAddOns?.length === 0) {
      // there is no parent and T20 is standalone then just by pass the parent
      return true;
    }
    if (parentAddOns?.length > 0) {
      const [criclife , starzplaysports] = parentAddOns || [];
      const isCricketActive = getAppState().addons.find(addon => (addon.name === criclife || addon.name === starzplaysports) && addon.status.toLowerCase() === "active")
      return isCricketActive ? true : false;
    }
    return false;
  }
  // is stand alone true
  return true;
}

export function T20ParentAddOn(addOnName) {
  const t20AddOn = getAddOnByName(addOnName);
  const parentAddOns = t20AddOn.configuration.subscriptionInherits !== "" ? t20AddOn.configuration.subscriptionInherits?.split(',') : [];
  if (parentAddOns?.length > 0) {
    const [criclife , starzplaysports] = parentAddOns || [];
    return getAddOnByName(criclife) || getAddOnByName(starzplaysports)
  }
}
